import './polyfills'
import { BaseElement } from '@nrk/dh-server-libs/base-element'
import { getElementTag } from '@nrk/dh-server-libs/get-element-tag'

import LevendeStillbilde from '@nrk/dh-basis-levende-stillbilde'
import StegvisVideo from '@nrk/dh-basis-stegvis-video'

import { HistorieScroll } from './components/app'

import './components/vars.module.css'
import './components/app.module.css'
import './components/app.template-adjustments.module.css'
import './components/media/media.module.css'
import './components/media/media-types.module.css'
import './components/media/shared/object-fit.module.css'
import './components/media/shared/scroll-progress.module.css'
import './components/cards/cards.module.css'
import './components/cards/alt-text.module.css'

function safeDefine(elementName, constructor) {
  if (typeof customElements.get(elementName) === 'undefined') {
    customElements.define(elementName, constructor)
  }
}

const tag = getElementTag(__APP_NAME__, __APP_VERSION__)

class AppElement extends BaseElement {
  name = tag
  #observer

  connectedCallback() {
    super.connectedCallback()

    this.addEventListener('dh-basis-scrollhistorie-trigger-activated', this.#handleTriggerActivated)
    this.#startCompletionLogger()
  }

  disconnectedCallback() {
    this.removeEventListener(
      'dh-basis-scrollhistorie-trigger-activated',
      this.#handleTriggerActivated
    )
    this.#stopCompletionLogger()
  }

  async render() {
    // Register custom elements as needed
    safeDefine('dhhs-scrollhistorie', HistorieScroll)
    safeDefine('dhhs-levende-stillbilde', LevendeStillbilde)
    safeDefine('dhhs-stegvis-video', StegvisVideo)

    if (
      this.features.video &&
      (typeof customElements.get('dhhs-article-player') === 'undefined' ||
        typeof customElements.get('dhhs-video') === 'undefined')
    ) {
      const { AutoplayOnActivePlayerElement, Video } = await import(
        './components/media/video' /* webpackChunkName: "dhhs-video" */
      )
      safeDefine('dhhs-article-player', AutoplayOnActivePlayerElement)
      safeDefine('dhhs-video', Video)
    }

    if (this.features.lottie && typeof customElements.get('dhhs-stegvis-lottie') === 'undefined') {
      const stegvisLottie = (
        await import('@nrk/dh-basis-stegvis-lottie' /* webpackChunkName: "dh-stegvis-lottie" */)
      ).default

      safeDefine('dhhs-stegvis-lottie', stegvisLottie)
    }

    if (this.features.kart && typeof customElements.get('dhhs-kart') === 'undefined') {
      const kart = (
        await import('./lib/karthistorie-chunk' /* webpackChunkName: "dh-karthistorie" */)
      ).default

      safeDefine('dhhs-kart', kart)
    }

    if (this.trackingEnabled) {
      this.sendGaEvent({ eventAction: `MOUNT:${tag}` })
    }

    if (this.features.sanityPreview) {
      import('./components/preview/preview-iframe-api' /* webpackChunkName: "dh-preview-iframe" */)
    }
  }

  #handleTriggerActivated = (e) => {
    this.sendGaEvent({ eventAction: 'TRIGGER-ACTIVATED', eventLabel: e.detail.triggerIndex })
  }

  #startCompletionLogger() {
    let hasStarted = false
    let hasCompleted = false

    // eslint-disable-next-line compat/compat
    this.#observer = new IntersectionObserver(
      (intersections) => {
        let isIntersecting = intersections[0]?.isIntersecting
        if (!hasStarted && isIntersecting && this.getBoundingClientRect().top > 0) {
          hasStarted = true
          this.sendGaEvent({ eventAction: `STARTED` })
        } else if (
          hasStarted &&
          !hasCompleted &&
          !isIntersecting &&
          this.getBoundingClientRect().bottom < 0
        ) {
          hasCompleted = true
          this.sendGaEvent({ eventAction: `COMPLETED` })
        }
      },
      { threshold: [0] }
    )
    this.#observer.observe(this)
  }

  #stopCompletionLogger() {
    this.#observer.disconnect()
  }

  get trackingEnabled() {
    return this.getAttribute('data-tracking-enabled') === 'true'
  }

  get features() {
    return JSON.parse(this.getAttribute('features'))
  }
}

if (!customElements.get(tag)) {
  customElements.define(tag, AppElement)
}
